import type { CSSProperties } from '@mantine/core'
import type { OrderCardProps } from './OrderCard.types'
import { Box, Button, Flex, Group, Text } from '@mantine/core'
import { clsx } from 'clsx'
import { extractRgbValues } from '@/shared/lib/mantine/mantine.utils'
import { useIsLgUp } from '@/shared/lib/style/breakpoints'
import classes from './OrderCard.module.css'

export function OrderCard({
  order,
  loading,
  actionText,
  className,
  animate,
  outline,
  color,
  statusText,
  statusColor,
  onClick,
  onAction,
  renderStatus,
  dataOnboarding,
  subtitle,
}: OrderCardProps) {
  const isLgUp = useIsLgUp()

  const pulseStyle = (): CSSProperties | undefined => {
    const effectiveColor = color || statusColor

    if (!effectiveColor || (!animate && !outline))
      return

    const _pulseColorRGBA = `rgba(${extractRgbValues(effectiveColor)}, 0.7)`
    const _pulseColorRGBValues = extractRgbValues(effectiveColor, false)

    return {
      '--status-color': _pulseColorRGBA,
      '--pulse-color': _pulseColorRGBValues,
    } as CSSProperties
  }

  function handleCardClick() {
    if (loading) {
      return
    }

    if (onClick !== undefined) {
      onClick(order)
    }
  }

  function handleActionClick(e: React.MouseEvent) {
    e.stopPropagation()

    if (loading) {
      return
    }

    if (onAction !== undefined) {
      onAction()
    }
  }

  return (
    <div
      className={clsx({
        [classes.orderCard]: true,
        [classes.cardOutline]: (animate || outline) && (color || statusColor),
        [classes.cardAnimationPulse]: animate && (color || statusColor),
        [classes.cursorDisabled]: loading,
        [className as string]: !!className,
      })}
      style={pulseStyle()}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      data-testid="order-card"
      data-onboarding={dataOnboarding}
    >
      <Group justify="space-between" wrap="nowrap" align="flex-start" gap="4px">
        <Flex direction="column" miw={0}>
          <Text
            span
            className={classes.customerName}
          >
            {order.customerName}
          </Text>
          <Text
            span
            className={classes.orderId}
          >
            {order.orderId}
          </Text>
          {subtitle && (
            <Text
              span
              size="xs"
              mt={4}
              c="gray.6"
            >
              {subtitle}
            </Text>
          )}
        </Flex>

        {renderStatus
          ? renderStatus()
          : statusText && statusColor
            ? (
                <Box
                  px="xs"
                  py={4}
                  fw={600}
                  bg={statusColor}
                  c="white"
                  tt="uppercase"
                >
                  {statusText}
                </Box>
              )
            : (
                <Text
                  span
                  size="sm"
                  lh="normal"
                  fw={600}
                  className={classes.statusText}
                >
                  {actionText}
                </Text>
              )}
      </Group>

      <Group justify="flex-end" mt="xs">
        {actionText && onAction && (
          <Button
            loading={loading}
            color={color || statusColor || 'violet'}
            variant="outline"
            size={isLgUp ? 'md' : 'sm'}
            className={classes.actionButton}
            onClick={handleActionClick}
          >
            {actionText}
          </Button>
        )}
      </Group>
    </div>
  )
}
